/**
 * `/settings/preferences` — per-device dashboard preferences.
 *
 * Appearance (Light / Dark / System) is the whole page today. The
 * metric-column pickers that used to live here as two ~26-checkbox
 * walls were redundant: `CustomizeMetricsPopover` next to each list
 * already covers the per-user override AND the admin "Apply for
 * everyone" workspace default — the loop is shorter where the columns
 * actually are.
 */
import { type ThemePref, setThemePref, useThemePref } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { Desktop, Moon, Sun } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/settings/preferences')({
  component: PreferencesPage,
});

function PreferencesPage(): ReactElement {
  return (
    <div className="max-w-xl space-y-10">
      <AppearanceSection />
    </div>
  );
}

/**
 * Appearance — the Light / Dark / System theme choice, and the only
 * place it lives (the account menu used to duplicate it). Per-device
 * (localStorage via src/lib/theme.ts, no API write), so it works in the
 * read-only demo build too.
 */
const THEME_OPTIONS: ReadonlyArray<{ value: ThemePref; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Desktop },
];

function AppearanceSection(): ReactElement {
  const themePref = useThemePref();
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-semibold text-[var(--color-ink)] text-sm">Appearance</h2>
        <p className="mt-1 text-[var(--color-mute)] text-xs">
          How the dashboard looks on this device. System follows your OS setting.
        </p>
      </div>
      <div
        // biome-ignore lint/a11y/useSemanticElements: segmented control is a button row + role=group
        role="group"
        aria-label="Theme"
        className="flex gap-2"
      >
        {THEME_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = themePref === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => setThemePref(opt.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs outline-none transition-colors',
                'focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40',
                active
                  ? 'border-[var(--color-line-strong)] bg-[var(--color-paper-alt)] text-[var(--color-ink)]'
                  : 'border-[var(--color-line)] text-[var(--color-mute)] hover:border-[var(--color-line-strong)] hover:text-[var(--color-ink)]',
              )}
            >
              <Icon aria-hidden="true" size={13} />
              {opt.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
